import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClientSignIn } from "../axios/api.js";

// Achievements received by the logged-in user
export const fetchMyAchievements = createAsyncThunk(
  "achievements/fetchMy",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get("/achievements/my");
      return response.data.achievements || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load achievements.");
    }
  }
);

// Achievements the logged-in user has GIVEN to others — the flip side of "my achievements".
export const fetchGivenAchievements = createAsyncThunk(
  "achievements/fetchGiven",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get("/achievements/given");
      return response.data.achievements || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load given achievements.");
    }
  }
);

// Recent achievements from the logged-in user's own department — the shared
// "recognition wall" everyone in the department can see, not just the recipient.
export const fetchDepartmentFeed = createAsyncThunk(
  "achievements/fetchFeed",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get("/achievements/feed");
      return response.data.achievements || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load the department feed.");
    }
  }
);

// Achievements received by any given user (e.g. viewing a colleague's profile)
export const fetchUserAchievements = createAsyncThunk(
  "achievements/fetchForUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/achievements/user/${userId}`);
      return response.data.achievements || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load achievements.");
    }
  }
);

// SuperAdmin only — achievements across many users at once, with optional filters
// (department, source, recipientId). Regular users will get a 403 from the API.
export const fetchAllAchievements = createAsyncThunk(
  "achievements/fetchAll",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get("/achievements/all", { params: filters });
      return response.data; // { achievements, total }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load achievements.");
    }
  }
);

// Same-department colleagues this user can give an achievement to
export const fetchGiveableColleagues = createAsyncThunk(
  "achievements/fetchColleagues",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get("/achievements/colleagues");
      return response.data.users || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load colleagues.");
    }
  }
);

export const giveAchievement = createAsyncThunk(
  "achievements/give",
  async ({ recipientId, title, message, icon }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosClientSignIn.post("/achievements/give", {
        recipientId, title, message, icon,
      });
      dispatch(fetchGivenAchievements()); // so the "Given by Me" tab reflects it immediately
      dispatch(fetchDepartmentFeed()); // so the shared feed shows it right away too
      return response.data.achievement;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to give achievement.");
    }
  }
);

const achievementSlice = createSlice({
  name: "achievements",
  initialState: {
    myAchievements: [],
    myAchievementsLoading: false,

    givenAchievements: [],
    givenAchievementsLoading: false,

    departmentFeed: [],
    departmentFeedLoading: false,

    viewedUserAchievements: [],
    viewedUserAchievementsLoading: false,

    allAchievements: [],
    allAchievementsTotal: 0,
    allAchievementsLoading: false,

    colleagues: [],
    colleaguesLoading: false,

    giving: false,
    giveError: null,
  },
  reducers: {
    clearGiveError: (state) => {
      state.giveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyAchievements.pending, (state) => {
        state.myAchievementsLoading = true;
      })
      .addCase(fetchMyAchievements.fulfilled, (state, action) => {
        state.myAchievementsLoading = false;
        state.myAchievements = action.payload;
      })
      .addCase(fetchMyAchievements.rejected, (state) => {
        state.myAchievementsLoading = false;
      })

      .addCase(fetchGivenAchievements.pending, (state) => {
        state.givenAchievementsLoading = true;
      })
      .addCase(fetchGivenAchievements.fulfilled, (state, action) => {
        state.givenAchievementsLoading = false;
        state.givenAchievements = action.payload;
      })
      .addCase(fetchGivenAchievements.rejected, (state) => {
        state.givenAchievementsLoading = false;
      })

      .addCase(fetchDepartmentFeed.pending, (state) => {
        state.departmentFeedLoading = true;
      })
      .addCase(fetchDepartmentFeed.fulfilled, (state, action) => {
        state.departmentFeedLoading = false;
        state.departmentFeed = action.payload;
      })
      .addCase(fetchDepartmentFeed.rejected, (state) => {
        state.departmentFeedLoading = false;
      })

      .addCase(fetchUserAchievements.pending, (state) => {
        state.viewedUserAchievementsLoading = true;
      })
      .addCase(fetchUserAchievements.fulfilled, (state, action) => {
        state.viewedUserAchievementsLoading = false;
        state.viewedUserAchievements = action.payload;
      })
      .addCase(fetchUserAchievements.rejected, (state) => {
        state.viewedUserAchievementsLoading = false;
      })

      .addCase(fetchAllAchievements.pending, (state) => {
        state.allAchievementsLoading = true;
      })
      .addCase(fetchAllAchievements.fulfilled, (state, action) => {
        state.allAchievementsLoading = false;
        state.allAchievements = action.payload.achievements || [];
        state.allAchievementsTotal = action.payload.total || 0;
      })
      .addCase(fetchAllAchievements.rejected, (state) => {
        state.allAchievementsLoading = false;
      })

      .addCase(fetchGiveableColleagues.pending, (state) => {
        state.colleaguesLoading = true;
      })
      .addCase(fetchGiveableColleagues.fulfilled, (state, action) => {
        state.colleaguesLoading = false;
        state.colleagues = action.payload;
      })
      .addCase(fetchGiveableColleagues.rejected, (state) => {
        state.colleaguesLoading = false;
      })

      .addCase(giveAchievement.pending, (state) => {
        state.giving = true;
        state.giveError = null;
      })
      .addCase(giveAchievement.fulfilled, (state) => {
        state.giving = false;
      })
      .addCase(giveAchievement.rejected, (state, action) => {
        state.giving = false;
        state.giveError = action.payload;
      });
  },
});

export const { clearGiveError } = achievementSlice.actions;
export default achievementSlice.reducer;
